import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Suspense, lazy, useEffect } from "react";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { HeroSkeleton, ProductGridSkeleton } from "@/components/Skeletons";
import { AuthProvider } from "@/context/AuthContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { RegionProvider } from "@/context/RegionContext";
import { SiteCategoriesProvider } from "@/context/SiteCategoriesContext";
import { SiteSettingsProvider, useSiteSettings } from "@/context/SiteSettingsContext";
import { ShopifyProvider } from "@/context/ShopifyContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { QueryProvider } from "@/providers/QueryProvider";
import GlobalLoginModal from "@/components/GlobalLoginModal";
import ComingSoonPage from "@/pages/ComingSoonPage";
import OfflineIndicator from "@/components/OfflineIndicator";
import ScrollManager from "@/components/common/ScrollManager";
import CartDrawer from "@/components/CartDrawer";

// Core pages - loaded immediately
import HomePage from "@/pages/HomePage";
import ProductGroupDetailPage from "@/pages/ProductGroupDetailPage";
import SearchPage from "@/pages/SearchPage";

// Lazy-loaded pages for code splitting
const DynamicCategoryPage = lazy(() => import("@/pages/DynamicCategoryPage"));
const DynamicSubcategoryPage = lazy(() => import("@/pages/DynamicSubcategoryPage"));
const DynamicStaticPage = lazy(() => import("@/pages/DynamicStaticPage"));
const EditorsPicksPage = lazy(() => import("@/pages/EditorsPicksPage"));
const AboutUsPage = lazy(() => import("@/pages/AboutUsPage"));
const AffiliateDisclosurePage = lazy(() => import("@/pages/AffiliateDisclosurePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallbackPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const FavoritesPage = lazy(() => import("@/pages/FavoritesPage"));

// Admin pages - lazy loaded (rarely used by most visitors)
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const SiteContentManagerPage = lazy(() => import("@/pages/SiteContentManagerPage"));
const SubcategoryManagerPage = lazy(() => import("@/pages/SubcategoryManagerPage"));
const EditorsPicksManagerPage = lazy(() => import("@/pages/EditorsPicksManagerPage"));
const AnnouncementManagerPage = lazy(() => import("@/pages/AnnouncementManagerPage"));
const CategoryManagerPage = lazy(() => import("@/pages/CategoryManagerPage"));
const StaticPagesManagerPage = lazy(() => import("@/pages/StaticPagesManagerPage"));
const UserManagementPage = lazy(() => import("@/pages/UserManagementPage"));
const NewsletterAdminPage = lazy(() => import("@/pages/admin/NewsletterAdminPage"));
const ApiStatsPage = lazy(() => import("@/pages/admin/ApiStatsPage"));
const ProductAnalyticsDashboard = lazy(() => import("@/pages/admin/ProductAnalyticsDashboard"));
const ProductTagsManagerPage = lazy(() => import("@/pages/admin/ProductTagsManagerPage"));
const ImportExportPage = lazy(() => import("@/pages/admin/ImportExportPage"));
const ImagesReportPage = lazy(() => import("@/pages/admin/ImagesReportPage"));
const ShopifyOrdersPage = lazy(() => import("@/pages/admin/ShopifyOrdersPage"));
const MaintenancePage = lazy(() => import("@/pages/MaintenancePage"));

// Loading fallbacks
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-content-muted">Loading...</div>
  </div>
);

const AdminLoader = () => (
  <div className="min-h-screen bg-surface-admin flex items-center justify-center">
    <div className="animate-pulse text-content-muted">Loading admin...</div>
  </div>
);

// Router component that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  const { settings, loading: settingsLoading } = useSiteSettings();
  
  // Check URL fragment for session_id (from Google OAuth callback)
  // This must be synchronous during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallbackPage />
      </Suspense>
    );
  }

  // Check if we're in Coming Soon or Maintenance mode (but allow admin routes)
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Preview mode: Allow admins to bypass Coming Soon/Maintenance with ?preview=admin
  const searchParams = new URLSearchParams(location.search);
  const isPreviewMode = searchParams.get('preview') === 'admin';
  const hasAdminToken = !!localStorage.getItem('admin_token');
  const canBypassModes = isAdminRoute || (isPreviewMode && hasAdminToken);
  
  const isComingSoon = settings.coming_soon_mode && !canBypassModes;
  const isMaintenance = settings.maintenance_mode && !canBypassModes;
  
  // Coming Soon takes priority over Maintenance mode
  if (!settingsLoading && isComingSoon) {
    return (
      <ComingSoonPage 
        launchDate={settings.coming_soon_launch_date}
        tagline={settings.coming_soon_tagline}
      />
    );
  }
  
  // Maintenance mode check
  if (!settingsLoading && isMaintenance) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MaintenancePage 
          message={settings.maintenance_message}
          estimatedTime={settings.maintenance_estimated_time}
        />
      </Suspense>
    );
  }

  return (
    <Routes>
      {/* Admin routes without Layout */}
      <Route path="/admin" element={<Navigate to="/admin/site-content" replace />} />
      <Route path="/admin/site-content" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary message="Failed to load admin page">
            <SiteContentManagerPage />
          </ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/products" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><AdminPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/subcategories" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><SubcategoryManagerPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/editors-picks" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><EditorsPicksManagerPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/announcements" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><AnnouncementManagerPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/categories" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><CategoryManagerPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/static-pages" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><StaticPagesManagerPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/users" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><UserManagementPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/newsletter" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><NewsletterAdminPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/api-stats" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><ApiStatsPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/product-analytics" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><ProductAnalyticsDashboard /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/product-tags" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><ProductTagsManagerPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/import-export" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><ImportExportPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/images-report" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><ImagesReportPage /></ErrorBoundary>
        </Suspense>
      } />
      <Route path="/admin/shopify-orders" element={
        <Suspense fallback={<AdminLoader />}>
          <ErrorBoundary><ShopifyOrdersPage /></ErrorBoundary>
        </Suspense>
      } />
      
      {/* Auth callback route */}
      <Route path="/auth/callback" element={
        <Suspense fallback={<PageLoader />}>
          <AuthCallbackPage />
        </Suspense>
      } />
      
      {/* Main site routes with Layout */}
      <Route path="/*" element={
        <Layout>
          <ErrorBoundary message="Something went wrong. Please refresh the page.">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/editors-picks" element={
                <Suspense fallback={<HeroSkeleton />}>
                  <EditorsPicksPage />
                </Suspense>
              } />
              <Route path="/about" element={
                <Suspense fallback={<PageLoader />}>
                  <AboutUsPage />
                </Suspense>
              } />
              <Route path="/affiliate-disclosure" element={
                <Suspense fallback={<PageLoader />}>
                  <AffiliateDisclosurePage />
                </Suspense>
              } />
              <Route path="/privacy-policy" element={
                <Suspense fallback={<PageLoader />}>
                  <PrivacyPolicyPage />
                </Suspense>
              } />
              <Route path="/profile" element={
                <Suspense fallback={<PageLoader />}>
                  <ProfilePage />
                </Suspense>
              } />
              <Route path="/favorites" element={
                <Suspense fallback={<PageLoader />}>
                  <FavoritesPage />
                </Suspense>
              } />
              <Route path="/product/*" element={<ProductGroupDetailPage />} />
              {/* Dynamic category and subcategory routes */}
              <Route path="/:categorySlug" element={
                <Suspense fallback={<ProductGridSkeleton />}>
                  <DynamicCategoryPage />
                </Suspense>
              } />
              <Route path="/:categorySlug/:subcategoryId" element={
                <Suspense fallback={<ProductGridSkeleton />}>
                  <DynamicSubcategoryPage />
                </Suspense>
              } />
              {/* Product detail page with canonical URL structure */}
              <Route path="/:categorySlug/:subcategorySlug/:productSlug" element={<ProductGroupDetailPage />} />
              {/* Legacy routes - redirect to search */}
              <Route path="/shop" element={<Navigate to="/search" replace />} />
              <Route path="/products" element={<Navigate to="/search" replace />} />
              <Route path="/products/:category" element={<Navigate to="/search" replace />} />
            </Routes>
          </ErrorBoundary>
        </Layout>
      } />
    </Routes>
  );
}

function App() {
  // VERSION MARKER - If you see this in console, new code is deployed
  console.log(`%c[BUILD: ${process.env.REACT_APP_VERSION || '5.5'}]`, 'color: red; font-size: 12px; font-weight: bold;');
  
  // Track external referrer on first page load
  useEffect(() => {
    const referrer = document.referrer;
    // Only track if referrer exists and is from external source
    if (referrer && !referrer.includes(window.location.hostname)) {
      // Send to backend
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track-referrer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          referrer,
          landing_page: window.location.pathname 
        })
      }).catch(() => {}); // Fail silently
    }
  }, []);
  
  return (
    <QueryProvider>
      <RegionProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <ScrollManager />
            <SiteSettingsProvider>
              <SiteCategoriesProvider>
                <AuthProvider>
                  <WishlistProvider>
                    <ShopifyProvider>
                      <div className="App">
                        <AppRouter />
                        <GlobalLoginModal />
                        <CartDrawer />
                        <Toaster position="bottom-right" />
                        <OfflineIndicator />
                      </div>
                    </ShopifyProvider>
                  </WishlistProvider>
                </AuthProvider>
              </SiteCategoriesProvider>
            </SiteSettingsProvider>
          </BrowserRouter>
        </CurrencyProvider>
      </RegionProvider>
    </QueryProvider>
  );
}

export default App;
